import type { KanbanTaskUpdateSubmitPayload } from '../../api/kanban-task-update-submit.hook'

import { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'

import { useToast } from '@shared/ui/toast'

import { useTasksKanbanStore } from '../store'
import { useKanbanTaskUpdateSubmitApi } from '../../api/kanban-task-update-submit.hook'

interface ButtonTaskUpdateReturn {
  setSubmitUpdate: () => Promise<void>
}

const isFormValid = (payload: KanbanTaskUpdateSubmitPayload): boolean => {
  if (!payload.status) {
    return false
  }
  if (
    !payload.deadline &&
    payload.status !== TasksKanbanStage.Resolved &&
    payload.status !== TasksKanbanStage.Cancelled
  ) {
    return false
  }

  if (payload.status === TasksKanbanStage.Waiting && !payload.waiting_reason) return false
  if (payload.status === TasksKanbanStage.Cancelled && !payload.cancel_reason) return false
  if (payload.status === TasksKanbanStage.Resolved && (!payload.resolution || !payload.customer_outcome)) return false

  return true
}

export const useButtonTaskUpdate = (): ButtonTaskUpdateReturn => {
  const tasksKanbanStore = useTasksKanbanStore()
  const toast = useToast()
  const route = useRoute()

  const buildPayload = (): KanbanTaskUpdateSubmitPayload => {
    const status = tasksKanbanStore.getStage!

    const payload: KanbanTaskUpdateSubmitPayload = {
      status,
      priority: tasksKanbanStore.getPriority,
      deadline: tasksKanbanStore.getDeadline,
      assignee_id: tasksKanbanStore.getAssignee?.id ?? null,
      extra_fields: tasksKanbanStore.getExtraFieldsPayload,
    }

    if (status === TasksKanbanStage.Waiting) {
      payload.waiting_reason = tasksKanbanStore.getWaitingReason
    }
    if (status === TasksKanbanStage.Cancelled) {
      payload.cancel_reason = tasksKanbanStore.getCancelReason
    }
    if (status === TasksKanbanStage.Resolved) {
      payload.resolution = tasksKanbanStore.getResolution
      payload.customer_outcome = tasksKanbanStore.getCustomerOutcome
    }

    return payload
  }

  const setSubmitUpdate = async (): Promise<void> => {
    tasksKanbanStore.setSubmitted(true)

    const id = route.params.id
    if (typeof id !== 'string' || !id) return

    const payload = buildPayload()

    if (!isFormValid(payload)) return

    if (!tasksKanbanStore.isExtraFieldsValid) {
      toast.warning({
        title: 'Attention!',
        description: 'Please fill in all Additional details fields.',
      })
      return
    }

    await useKanbanTaskUpdateSubmitApi({
      id,
      task: payload,
      onSuccess: () => {
        tasksKanbanStore.setSubmitted(false)
        toast.success('Task updated successfully')
      },
    })
  }

  return {
    setSubmitUpdate,
  }
}
